import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";

import PreviewThumbnail from "@/features/funnel/components/PreviewThumbnail";
import { DeploymentStatusTag } from "@/features/react-editor/components/DeploymentStatusTag";
import { EditablePageName } from "@/features/react-editor/components/navbar/EditablePageNameWithDropdown";
import usePublishedSiteUrls from "@/features/react-editor/hooks/usePublishedSiteUrls";
import IconButton from "@replo/design-system/components/button/IconButton";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChartLine, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

type PageCardProps = {
  file: FileWithLatestFileVersion;
  layoutClassName?: string;
};

const PageCard: React.FC<PageCardProps> = ({ file, layoutClassName }) => {
  const { project } = useCurrentProjectContext();
  const [isRenamingPage, setIsRenamingPage] = React.useState(false);

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: [file.pageRoute ?? ""],
  });

  const publishedSiteUrl = publishedSiteUrls[0];

  if (!project) {
    return null;
  }

  return (
    <div className="h-full">
      <Link to={`/shops/${project.id}/chat/?pageId=${file.id}`}>
        <div
          className={twMerge(
            "border-0.5 border-border rounded-lg bg-sheet hover:bg-menu-item-hover flex flex-col cursor-pointer shadow-sm transition-shadow h-full",
            layoutClassName,
          )}
        >
          <div className="p-4 pb-0 flex flex-col gap-2">
            <div className="flex justify-center gap-4 rounded-b-lg h-[155px] overflow-hidden">
              <PreviewThumbnail
                key={file.id}
                pageRoute={file.pageRoute ?? ""}
                className="w-[200px] h-[360px]"
              />
            </div>
          </div>
          <div className="p-3 flex justify-between items-center border-t-0.5 border-border bg-white rounded-lg">
            <div className="flex items-center gap-1.5">
              {isRenamingPage ? (
                <EditablePageName
                  page={file}
                  projectId={project.id}
                  setIsRenaming={setIsRenamingPage}
                />
              ) : (
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsRenamingPage(true);
                  }}
                  className="header-h4 text-default line-clamp-1"
                >
                  {file.name}
                </div>
              )}
              <DeploymentStatusTag variant="page" file={file} />
            </div>
            <div className="flex items-center gap-1">
              <Tooltip triggerAsChild content="View funnel analytics">
                <IconButton
                  variant="secondary"
                  size="base"
                  icon={<ChartLine size={16} />}
                />
              </Tooltip>
              <IconButton
                variant="secondary"
                size="base"
                icon={<ExternalLink size={16} />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(publishedSiteUrl, "_blank");
                }}
              />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default PageCard;
